import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Modal,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { GridCloseIcon } from "@mui/x-data-grid";
import DeleteIcon from '@mui/icons-material/Delete';
import MainCard from '../../UI/MainCard';
import NavigationPanel from '../../UI/NavigationPanel';
import AppointmentService from '../../services/AppointmentService';
import CompanyService from '../../services/CompanyService';
import ConfigurationService from '../../services/ConfigurationService';
import { OPERATION_3020 } from "../../utils/constants";

const styles = {
    subTitle: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    boldText: {
        fontWeight: 'bold',
        fontSize: '14px',
        boxSizing: 'border-box',
    },
    tableContainer: {
        margin: '20px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
    },
    headerRow: {
        backgroundColor: '#f5f5f5',
        fontWeight: 'bold'
    },
    cell: {
        border: '1px solid #ccc',
        height: '80px',
        width: '300px',
        boxSizing: 'border-box',
        padding: '10px',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' }
    },
    appointmentContent: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
    },
    companyBadge: {
        fontSize: '12px',
        fontWeight: 'bold',
        color: '#fff',
        padding: '2px 5px',
        borderRadius: '3px',
        marginTop: '5px'
    },
    modal: {
        position: 'absolute',
        width: '400px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        padding: '20px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
    },
    modalInput: {
        marginBottom: '16px',
        width: '100%'
    },
    headerColumn: {
        backgroundColor: '#f5f5f5',
        fontWeight: 'bold'
    },
    // Balanced green and brown color palette for different companies (10 distinct colors)
    companyColors: [
        '#2e8b57', // Sea Green
        '#8b4513', // Saddle Brown
        '#228b22', // Forest Green
        '#a0522d', // Sienna
        '#32cd32', // Lime Green
        '#cd853f', // Peru
        '#556b2f', // Dark Olive Green
        '#8b6914', // Dark Goldenrod
        '#90ee90', // Light Green
        '#d2691e', // Chocolate
    ]
};

const Fixed3020Schedule = () => {
    const { t } = useTranslation();
    const appointmentService = new AppointmentService();
    const companyService = new CompanyService();
    const configurationService = new ConfigurationService();
    const [appointments, setAppointments] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [open, setOpen] = useState(false);
    const [editedData, setEditedData] = useState(null);
    const [panels,] = useState([t('adminPanel'), t('freightOperations'), t('fixed3020Schedule')]);
    const [loading, setLoading] = useState(false);
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    const [slots, setSlots] = useState([]);
    const [updateSchedule, setUpdateSchedule] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [operation, setOperation] = useState(OPERATION_3020.SHIPMENT);
    const [deleteSchedule, setDeleteSchedule] = useState([]);

    // Company search functionality
    const [companySearchParams, setCompanySearchParams] = useState({});
    const [companySearchParamsLength, setCompanySearchParamsLength] = useState(0);

    useEffect(() => {
        fetchSlots();
        fetchAppointments();
        fetchCompanies();
    }, []);

    useEffect(() => {
        if (companySearchParams && companySearchParamsLength >= 2) {
            const params = {
                search: true,
                query: companySearchParams
            };
            fetchCompanies(params);
        } else if (companySearchParamsLength === 0) {
            fetchCompanies();
        }
    }, [companySearchParamsLength]);

    useEffect(() => {
        fetchAppointments();
    }, [operation]);

    useEffect(() => {
        setHasChanges(updateSchedule.length > 0 || deleteSchedule.length > 0);
    }, [updateSchedule, deleteSchedule]);

    const fetchAppointments = async () => {
        try {
            setLoading(true);
            const response = await appointmentService.getFixedSchedule(operation);
            setAppointments(response.data);
        } catch (error) {
            console.error('Failed to fetch appointments:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSlots = async () => {
        try {
            const response = await appointmentService.getSlots({ includeBreaks: true });
            setSlots(response.data);
        } catch (error) {
            console.error('Failed to fetch slots:', error);
        }
    };

    const fetchCompanies = async (params) => {
        try {
            const response = await companyService.getCompanies(params);
            setCompanies(response.data);
        } catch (error) {
            console.error('Failed to fetch companies:', error);
        }
    };

    const handleOpen = (appointment, day, slot) => {
        if (appointment) {
            // Editing existing appointment
            setEditedData({
                ...appointment,
                companyId: appointment.companyId || appointment.company?.id,
                companyName: appointment.companyName || appointment.company?.name
            });
        } else {
            // Creating new appointment for empty slot
            setEditedData({
                appointmentDay: day,
                weekDayIndex: days.indexOf(day), // Monday = 0, Tuesday = 1, etc.
                slotNumber: slot.SLOT_NUMBER,
                appointmentTime: slot.START,
                companyId: '',
                companyName: '',
                description: ''
            });
        }
        setOpen(true);
    };

    const handleUpdate = () => {
        try {
            // Find the selected company to get the full company data
            const selectedCompany = companies.find(c => c.id === editedData.companyId);
            const appointmentData = {
                appointmentDay: editedData.appointmentDay,
                weekDayIndex: editedData.weekDayIndex,
                slotNumber: editedData.slotNumber,
                appointmentTime: editedData.appointmentTime,
                companyId: editedData.companyId,
                companyName: selectedCompany ? selectedCompany.name : editedData.companyName,
                description: editedData.description
            };

            // Add to update schedule instead of immediate update
            setUpdateSchedule(prev => {
                const existingIndex = prev.findIndex(item =>
                    item.appointmentDay === appointmentData.appointmentDay &&
                    item.slotNumber === appointmentData.slotNumber
                );

                if (existingIndex >= 0) {
                    // Update existing entry
                    const newSchedule = [...prev];
                    newSchedule[existingIndex] = appointmentData;
                    return newSchedule;
                } else {
                    // Add new entry
                    return [...prev, appointmentData];
                }
            });

            // Update local state for immediate UI feedback
            const existingAppointment = appointments.find(a =>
                a.appointmentDay === editedData.appointmentDay &&
                a.slotNumber === editedData.slotNumber
            );

            if (existingAppointment) {
                // Update existing appointment
                setAppointments(prev => prev.map(a =>
                    a.appointmentDay === editedData.appointmentDay &&
                        a.slotNumber === editedData.slotNumber ? appointmentData : a
                ));
            } else {
                // Add new appointment
                setAppointments(prev => [...prev, appointmentData]);
            }

            setHasChanges(true);
            setOpen(false);
        } catch (error) {
            console.error('Update failed:', error);
        }
    };

    const handleDelete = (appointment) => {
        try {
            // Create minimal deletion data with only essential fields
            const deleteData = {
                weekDayIndex: appointment.weekDayIndex,
                slotNumber: appointment.slotNumber
            };

            // Add to delete schedule
            setDeleteSchedule(prev => {
                const existingIndex = prev.findIndex(item =>
                    item.weekDayIndex === deleteData.weekDayIndex &&
                    item.slotNumber === deleteData.slotNumber
                );

                if (existingIndex >= 0) {
                    // Already marked for deletion
                    return prev;
                } else {
                    // Add new deletion entry
                    return [...prev, deleteData];
                }
            });

            // Remove from appointments for immediate UI feedback
            setAppointments(prev => prev.filter(a =>
                !(a.appointmentDay === appointment.appointmentDay &&
                    a.slotNumber === appointment.slotNumber)
            ));

            setHasChanges(true);
        } catch (error) {
            console.error('Delete failed:', error);
        }
    };

    const handleSaveAllChanges = async () => {
        try {
            setLoading(true);

            // Send all updates in a single batch request
            await configurationService.updateFixed3020ScheduleBatch({ operation, updateSchedule, deleteSchedule });

            // Clear the schedules after successful save
            setUpdateSchedule([]);
            setDeleteSchedule([]);
            setHasChanges(false);

            // Refresh appointments to ensure consistency
            await fetchAppointments();

        } catch (error) {
            console.error('Batch update failed:', error);
        } finally {
            setLoading(false);
        }
    };

    // Ensure unique color assignment for companies
    const getUniqueCompanyColor = (companyName) => {
        if (!companyName) return '#95a5a6'; // Default gray

        // Get all unique company names from appointments
        const uniqueCompanies = Array.from(new Set(appointments.map(a => a.companyName))).filter(name => name);

        // Find the index of this company in the unique list
        const companyIndex = uniqueCompanies.indexOf(companyName);

        if (companyIndex === -1) return '#95a5a6'; // Default gray if not found

        // Assign color based on position in unique companies list
        const colorIndex = companyIndex % styles.companyColors.length;

        return styles.companyColors[colorIndex];
    };

    const getCompanyStyle = (companyName) => {
        const color = getUniqueCompanyColor(companyName);
        return {
            backgroundColor: color,
            color: '#ffffff',
            fontWeight: 'bold',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
        };
    };

    const getCellStyle = (companyName) => {
        const color = getUniqueCompanyColor(companyName);
        return {
            ...styles.cell,
            backgroundColor: color + '20', // Add transparency
            border: `2px solid ${color}`,
            '&:hover': {
                backgroundColor: color + '30',
                transform: 'scale(1.02)',
                transition: 'all 0.2s ease'
            }
        };
    };

    return (
        <MainCard isSingleCard={true}>
            <Grid container item sx={{ ...styles.subTitle }} xs={12}>
                <NavigationPanel panels={panels}></NavigationPanel>
            </Grid>

            <Grid container item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: '30px' }}>
                {/* Save Button */}
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveAllChanges}
                        disabled={!hasChanges || loading}
                        sx={{
                            minWidth: '120px',
                            backgroundColor: hasChanges ? '#1976d2' : '#ccc',
                            '&:hover': {
                                backgroundColor: hasChanges ? '#1565c0' : '#ccc'
                            }
                        }}
                    >
                        {loading ? <CircularProgress size={20} color="inherit" /> : t('saveConfiguration')}
                    </Button>
                </Grid>

                {/* Gate Number Selector */}
                <Grid item sx={{ pr: '20px', minWidth: '13rem' }}>
                    <FormControl fullWidth>
                        <InputLabel id="freight-area-label">{t('gateNumber')}</InputLabel>
                        <Select
                            value={operation}
                            label={t('operation')}
                            onChange={(e) => setOperation(e.target.value)}
                        >
                            <MenuItem value={OPERATION_3020.SHIPMENT}> {t('SHIPMENT')} </MenuItem>
                            <MenuItem value={OPERATION_3020.ACCEPTANCE}> {t('ACCEPTANCE')} </MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {/* Company Color Legend */}
            <Grid container item xs={12} sx={{ mt: 2, mb: 2 }}>
                <Grid item xs={12}>
                    <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                        <Grid container spacing={2} justifyContent="center">
                            {Array.from(new Set(appointments.map(a => a.companyName))).map((companyName) => {
                                if (companyName) {
                                    return (
                                        <Grid item key={companyName}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box
                                                    sx={{
                                                        width: 20,
                                                        height: 20,
                                                        backgroundColor: getUniqueCompanyColor(companyName),
                                                        borderRadius: '4px',
                                                        border: '2px solid #fff',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                    }}
                                                />
                                                <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 'bold' }}>
                                                    {companyName}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    );
                                }
                                return null;
                            })}
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>

            {loading ? (
                <Grid container item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: '20px' }}>
                    <CircularProgress />
                </Grid>
            ) : (
                <Grid container item xs={12}>
                    <TableContainer component={Paper} sx={styles.tableContainer}>
                        <Table>
                            <TableHead>
                                <TableRow sx={styles.headerRow}>
                                    <TableCell sx={styles.boldText}></TableCell>
                                    {days.map((day, index) => (
                                        <TableCell key={index} align="center" sx={{ ...styles.boldText, width: '300px' }}>
                                            <Typography variant="body1" sx={styles.boldText}>{t(day.toLowerCase())}</Typography>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {slots.map(slot => (
                                    <TableRow key={slot.SLOT_NUMBER}>
                                        <TableCell key={slot.SLOT_NUMBER} align="center" sx={{ ...styles.boldText, width: '150px' }}>
                                            {slot.START} - {slot.END}
                                        </TableCell>
                                        {days.map((day, index) => {
                                            const appointment = appointments.find(a =>
                                                a.appointmentDay === day && a.slotNumber === slot.SLOT_NUMBER
                                            );
                                            return (
                                                <TableCell
                                                    key={`${day}-${slot.SLOT_NUMBER}`}
                                                    align="center"
                                                    sx={appointment ? getCellStyle(appointment.companyName) : styles.cell}
                                                    onClick={() => handleOpen(appointment, day, slot)}
                                                >
                                                    {appointment && (
                                                        <Grid container item sx={styles.appointmentContent}>
                                                            <Typography variant="body2" sx={getCompanyStyle(appointment.companyName)}>
                                                                {appointment.description}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ ...styles.boldText, marginTop: '4px', fontSize: '12px' }}>
                                                                {appointment.companyName}
                                                            </Typography>
                                                        </Grid>
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            )}

            <Modal open={open} onClose={() => setOpen(false)}>
                <div style={styles.modal}>
                    <IconButton onClick={() => setOpen(false)} sx={{ float: 'right' }}>
                        <GridCloseIcon />
                    </IconButton>

                    {editedData && (
                        <>
                            <Typography variant="h6" gutterBottom>
                                {appointments.find(a =>
                                    a.appointmentDay === editedData.appointmentDay &&
                                    a.slotNumber === editedData.slotNumber
                                ) ? t('editAppointment') : t('createAppointment')}
                            </Typography>

                            <FormControl fullWidth sx={styles.modalInput}>
                                <InputLabel>{t('company')}</InputLabel>
                                <Select
                                    value={editedData.companyId || ''}
                                    label={t('company')}
                                    onChange={(e) => {
                                        const selectedCompany = companies.find(c => c.id === e.target.value);
                                        const updatedData = {
                                            ...editedData,
                                            companyId: e.target.value,
                                            companyName: selectedCompany ? selectedCompany.name : ''
                                        };
                                        setEditedData(updatedData);
                                    }}
                                >
                                    <MenuItem sx={{ p: 1 }}>
                                        <TextField
                                            size="small"
                                            placeholder={t('searchCompanies')}
                                            fullWidth
                                            onChange={(e) => {
                                                const searchTerm = e.target.value;
                                                setCompanySearchParamsLength(searchTerm.length);
                                                setCompanySearchParams(searchTerm);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => e.stopPropagation()}
                                        />
                                    </MenuItem>
                                    {companies.slice(0, 10).map((company) => (
                                        <MenuItem key={company.id} value={company.id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box
                                                    sx={{
                                                        width: 12,
                                                        height: 12,
                                                        backgroundColor: getUniqueCompanyColor(company.name),
                                                        borderRadius: '2px',
                                                        mr: 1
                                                    }}
                                                />
                                                {company.name}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                    {companies.length > 10 && (
                                        <MenuItem disabled sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                                            {t('showingFirst10Results')} ({companies.length} {t('total')})
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>

                            <TextField
                                label={t('description')}
                                value={editedData.description}
                                onChange={(e) => setEditedData({ ...editedData, description: e.target.value })}
                                sx={styles.modalInput}
                                fullWidth
                            />

                            <Grid container spacing={2} justifyContent="space-between">
                                <Grid item xs={appointments.find(a =>
                                    a.appointmentDay === editedData.appointmentDay &&
                                    a.slotNumber === editedData.slotNumber
                                ) ? 9 : 12}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleUpdate}
                                        fullWidth
                                        size="small"
                                        disabled={!editedData.companyId}
                                    >
                                        {appointments.find(a =>
                                            a.appointmentDay === editedData.appointmentDay &&
                                            a.slotNumber === editedData.slotNumber
                                        ) ? t('updateAppointment') : t('createAppointment')}
                                    </Button>
                                </Grid>
                                {appointments.find(a =>
                                    a.appointmentDay === editedData.appointmentDay &&
                                    a.slotNumber === editedData.slotNumber
                                ) && (
                                        <Grid item>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                onClick={() => {
                                                    handleDelete(editedData);
                                                    setOpen(false);
                                                }}
                                                startIcon={<DeleteIcon />}
                                            >
                                                {t('delete')}
                                            </Button>
                                        </Grid>
                                    )}
                            </Grid>
                        </>
                    )}
                </div>
            </Modal>
        </MainCard>
    );
};

export default Fixed3020Schedule;